import { PRIMARY_COLORS, TEXT_COLORS, theme } from "@monthly/theme";
import HighchartsReact from "highcharts-react-official";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { addWatermarkToHighchartsExport } from "../chartWatermark";
import Highcharts from "../HighChartsConfig";
import {
  formatNumberWithSeparators,
  truncateNumber,
} from "../utils/formatting";

interface SmallTrendChartProps {
  data: number[];
  color?: string;
  height?: number;
  categories?: string[];
  showTooltip?: boolean;
  labelSize?: "small" | "large";
  chartType?: "column" | "line";
  isPercentage?: boolean;
  showDataLabels?: boolean;
  showCategories?: boolean;
  decimalPlaces?: number;
  unidades?: string; // K | M | B
  showXAxisLabels?: boolean;
}

export interface SmallTrendChartRef {
  downloadImage: (filename?: string) => void;
}

export const SmallTrendChart = forwardRef<
  SmallTrendChartRef,
  SmallTrendChartProps
>(
  (
    {
      data,
      color = PRIMARY_COLORS.main,
      height = 80,
      categories,
      showTooltip = true,
      labelSize = "small",
      chartType = "column",
      isPercentage = false,
      showDataLabels = true,
      showCategories = true,
      decimalPlaces = 2,
      unidades = "M",
      showXAxisLabels = true,
    },
    ref,
  ) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const isLarge = labelSize === "large";

    useImperativeHandle(ref, () => ({
      downloadImage: (filename = "chart") => {
        if (chartRef.current?.chart) {
          addWatermarkToHighchartsExport(chartRef.current.chart, filename);
        }
      },
    }));

    const options: Highcharts.Options = {
      chart: {
        type: chartType,
        height,
        spacing: [0, 0, 0, 0],
        backgroundColor: "transparent",
      },
      title: {
        text: undefined,
      },
      xAxis: {
        visible: showCategories,
        categories: categories,
        labels: {
          enabled: showXAxisLabels,
          y: isLarge ? 18 : 12,
          style: {
            ...theme.typography.caption,
            color: theme.palette.text.primary,
          },
        },
        lineColor: theme.palette.text.primary,
        tickLength: 0,
      },
      yAxis: {
        visible: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: showTooltip,
        backgroundColor: "#FFFFFF",
        borderColor: "#E0E2E7",
        borderRadius: 8,
        shadow: {
          color: "rgba(155, 160, 185, 0.2)",
          offsetX: 0,
          offsetY: 2,
          width: 8,
        },
        style: {
          fontFamily: `${theme.typography.caption.fontFamily}`,
          fontSize: `${theme.typography.caption.fontSize}`,
          fontWeight: `${theme.typography.caption.fontWeight}`,
          color: theme.palette.text.primary,
        },
        formatter: function () {
          const point = this as unknown as {
            point: { index: number };
            y: number;
          };
          const period =
            categories?.[point.point.index] ||
            `Periodo ${point.point.index + 1}`;
          const value = formatNumberWithSeparators(
            point.y || 0,
            isPercentage ? 1 : decimalPlaces,
          );
          const suffix = isPercentage ? "%" : unidades || "";
          return `<div style="padding: 4px 0;">
          <span style="color: ${TEXT_COLORS.secondary}; font-size: 11px;">${period}</span><br/>
          <span style="color: ${color}; font-weight: 600; font-size: 14px;">${value}${suffix}</span>
        </div>`;
        },
        useHTML: true,
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        column: {
          borderRadius: 3,
          pointPadding: 0.08,
          groupPadding: 0.08,
          color: color,
          dataLabels: {
            enabled: true,
            formatter: function () {
              const value = formatNumberWithSeparators(
                (this as { y?: number }).y ?? 0,
                decimalPlaces,
              );
              return value;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: theme.palette.text.primary,
              textOutline: "none",
            },
            y: isLarge ? -4 : -2,
          },
          states: {
            hover: {
              brightness: -0.08,
            },
          },
        },
        line: {
          color: color,
          lineWidth: 2,
          marker: {
            enabled: true,
            radius: 4,
            fillColor: "#FFFFFF",
            lineWidth: 2,
            lineColor: color,
            symbol: "circle",
          },
          dataLabels: {
            enabled: showDataLabels,
            formatter: function () {
              const formatted = formatNumberWithSeparators(
                this.y ?? 0,
                decimalPlaces,
              );

              return isPercentage ? `${formatted}%` : formatted;
            },
            style: {
              fontFamily: `${theme.typography.caption.fontFamily}`,
              fontSize: `${theme.typography.caption.fontSize}`,
              fontWeight: `${theme.typography.caption.fontWeight}`,
              color: theme.palette.text.primary,
              textOutline: "none",
            },
            y: isLarge ? -6 : -4,
          },
          states: {
            hover: {
              lineWidth: 2,
            },
          },
        },
      },
      series: [
        {
          type: chartType,
          data: data.map((value) => truncateNumber(value, decimalPlaces)),
        } as Highcharts.SeriesOptionsType,
      ],
    };

    useEffect(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, []);

    useEffect(() => {
      // Force chart update when decimalPlaces changes
      if (chartRef.current?.chart) {
        chartRef.current.chart.update(
          {
            plotOptions: options.plotOptions,
          },
          true,
        );
      }
    }, [decimalPlaces]);

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    );
  },
);
